import { PortaliaMark } from '../components/PortaliaMark';
import React, { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/useAuthStore';
import { Menu, Transition } from '@headlessui/react';
import {
  LogOut,
  Trash2,
  User,
  LayoutDashboard,
  Receipt,
  History,
  Menu as MenuIcon,
  X,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useQueryClient } from '@tanstack/react-query';
import { firebaseSignOut } from '../services/firebase';
import { useAppliedTheme } from '../hooks/useAppliedTheme';
import { useTenantConfig } from '../hooks/useTenantConfig';
import { TenantSwitcher } from '../components/TenantSwitcher';
import { ThemeToggle } from '../components/ThemeToggle';
import { DeleteAccountDialog } from '../components/DeleteAccountDialog';

interface LayoutProps {
  children: ReactNode;
}

const CustomerPortalLayout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = React.useState(false);

  const handleLogout = async () => {
    // Firebase owns the session. Resetting only our store leaves its session
    // alive, and the auth watcher treats "Firebase user, no portal user" as a
    // social-redirect return and rebuilds the session — sign-out that does not
    // include the SDK is cosmetic. The cache goes too: this may be a shared
    // device, and the next sign-in must not flash the previous user's data.
    await firebaseSignOut();
    logout();
    navigate('/login');
    queryClient.clear();
  };

  const { data: tenantConfig } = useTenantConfig();

  // Apply tenant branding (fonts, colours) under the active scheme
  useAppliedTheme(
    tenantConfig
      ? {
          primaryColor: tenantConfig.primaryColor,
          accentColor: tenantConfig.accentColor,
          fontFamily: tenantConfig.fontFamily,
        }
      : undefined
  );

  // Active styling is driven by the tenant's brand colour. It reads the theme
  // variables directly rather than a Tailwind class so it stays correct in both
  // schemes — `--brand` is already contrast-corrected by resolveTheme().
  const activeLinkStyle: React.CSSProperties = {
    color: 'rgb(var(--brand))',
    backgroundColor: 'rgb(var(--brand-subtle))',
  };

  const navItems = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard },
    { name: 'My Invoices', href: '/invoices', icon: Receipt },
    { name: 'Payments', href: '/payments', icon: History },
  ];

  return (
    <div className="min-h-screen bg-canvas flex flex-col font-sans">
      {/* Navigation */}
      <nav className="sticky top-0 z-30 bg-surface/80 backdrop-blur-md border-b border-line-subtle">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-8">
              <Link to="/" className="flex items-center space-x-2 group">
                {tenantConfig?.logo ? (
                  <img
                    src={tenantConfig.logo}
                    alt={tenantConfig.companyName}
                    className="h-8 w-auto rounded-lg"
                  />
                ) : (
                  <PortaliaMark className="h-8 w-8 transition-transform group-hover:rotate-12" />
                )}
                <span className="text-lg font-black tracking-tight text-content">
                  {tenantConfig?.companyName || 'Portal'}
                </span>
              </Link>

              <div className="hidden md:flex items-center space-x-1">
                {navItems.map(item => {
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                        isActive
                          ? ''
                          : 'text-content-muted hover:text-content hover:bg-surface-muted'
                      }`}
                      style={isActive ? activeLinkStyle : undefined}
                    >
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center space-x-2">
              {/* Renders itself away unless more than one tenant is linked. */}
              <TenantSwitcher />

              <ThemeToggle />

              <Menu as="div" className="relative">
                <Menu.Button className="h-10 w-10 bg-surface-muted rounded-xl flex items-center justify-center text-content-muted hover:bg-surface-raised transition-colors">
                  <User size={20} />
                </Menu.Button>
                <Transition
                  as={React.Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right bg-surface rounded-2xl shadow-xl ring-1 ring-content ring-opacity-5 focus:outline-none p-2 border border-line-subtle">
                    <div className="px-4 py-3 border-b border-line-subtle mb-1">
                      <p className="text-[10px] font-black text-content-muted uppercase tracking-widest">
                        Signed in as
                      </p>
                      <p className="text-sm font-bold text-content truncate mt-0.5">
                        {user?.name || 'Customer'}
                      </p>
                    </div>

                    <div className="px-4 py-2 border-b border-line-subtle mb-1 flex items-center justify-between">
                      <span className="text-xs font-bold text-content-secondary">Theme</span>
                      <ThemeToggle />
                    </div>

                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={handleLogout}
                          className={`${
                            active ? 'bg-surface-muted text-content' : 'text-content-secondary'
                          } flex w-full items-center px-4 py-2.5 text-sm font-bold rounded-xl transition-colors`}
                        >
                          <LogOut size={18} className="mr-3" />
                          Logout
                        </button>
                      )}
                    </Menu.Item>

                    {/* App Store rules require account deletion to be reachable
                        in-app, not by emailing support. */}
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => setIsDeleteOpen(true)}
                          className={`${
                            active ? 'bg-danger-subtle' : ''
                          } flex w-full items-center px-4 py-2.5 text-sm font-bold text-danger rounded-xl transition-colors mt-1 border-t border-line-subtle pt-3`}
                        >
                          <Trash2 size={18} className="mr-3" />
                          Delete account
                        </button>
                      )}
                    </Menu.Item>
                  </Menu.Items>
                </Transition>
              </Menu>

              <button
                className="md:hidden p-2 text-content-muted"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                {isMobileMenuOpen ? <X size={24} /> : <MenuIcon size={24} />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-surface border-b border-line-subtle overflow-hidden"
          >
            <div className="px-4 py-4 space-y-1">
              {navItems.map(item => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center px-4 py-3 rounded-xl text-base font-bold ${
                      isActive ? '' : 'text-content-muted hover:bg-surface-muted'
                    }`}
                    style={isActive ? activeLinkStyle : undefined}
                  >
                    <item.icon size={20} className="mr-4" />
                    {item.name}
                  </Link>
                );
              })}
              <div className="pt-3 mt-2 border-t border-line-subtle flex items-center justify-between px-4 py-2">
                <span className="text-sm font-bold text-content-secondary">Appearance</span>
                <ThemeToggle />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="flex-1 max-w-5xl mx-auto px-4 sm:px-6 py-10 w-full">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {children}
        </motion.div>
      </main>

      <DeleteAccountDialog open={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} />

      {/* Footer */}
      <footer className="py-10 border-t border-line-subtle bg-surface">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <p className="text-xs font-bold text-content-muted uppercase tracking-widest">
            PT Alia Primavera
          </p>
        </div>
      </footer>
    </div>
  );
};

export default CustomerPortalLayout;
